import { UseCase } from '@/types/use-case';

// Sample response data based on our actual API test
const sampleApiResponse = {
  data: {
    timelines: [{
      timestep: "1h",
      intervals: [
        {
          time: "2024-12-21T18:00:00Z",
          values: {
            temperature: 18.6,
            humidity: 75,
            precipitationProbability: 0,
            rainIntensity: 0,
            evapotranspiration: 0.214,
            windSpeed: 4.4,
            windDirection: 70,
            dewPoint: 14.3,
            pressureSeaLevel: 1013.38
          }
        },
        {
          time: "2024-12-21T19:00:00Z",
          values: {
            temperature: 19.7,
            humidity: 69,
            precipitationProbability: 0,
            rainIntensity: 0,
            evapotranspiration: 0.361,
            windSpeed: 3.5,
            windDirection: 68,
            dewPoint: 13.8,
            pressureSeaLevel: 1014.0
          }
        },
        {
          time: "2024-12-21T20:00:00Z",
          values: {
            temperature: 20.0,
            humidity: 68,
            precipitationProbability: 0,
            rainIntensity: 0,
            evapotranspiration: 0.355,
            windSpeed: 3.4,
            windDirection: 76,
            dewPoint: 13.9,
            pressureSeaLevel: 1014.0
          }
        }
      ]
    }],
    location: {
      lat: 42.3601,
      lon: -71.0589
    }
  }
};

export const agriculturalIotUseCase: UseCase = {
  id: 'agricultural-iot',
  title: 'Agricultural IoT Dashboard',
  industry: 'Agriculture',
  complexity: 'Intermediate',
  description: 'Smart farm management dashboard that combines Tomorrow.io Weather API data with Chart.js visualizations for precision agriculture decisions.',
  problemStatement: 'Modern farms need real-time weather insights to optimize irrigation, protect crops from stress, and maximize yields. Traditional weather apps lack the precision and agricultural-specific metrics needed for data-driven farming decisions.',
  solutionOverview: 'This implementation demonstrates how to build a responsive dashboard that monitors temperature trends, humidity levels, and evapotranspiration rates to guide irrigation scheduling and crop management decisions.',
  
  apiRequest: {
    endpoint: '/weather/forecast',
    method: 'GET',
    parameters: {
      location: '42.3601,-71.0589',
      apikey: 'YOUR_API_KEY'
    }
  },
  
  apiResponse: {
    status: 200,
    data: sampleApiResponse.data,
    timestamp: new Date().toISOString()
  },
  
  implementationCode: {
    language: 'javascript',
    code: `// Transform weather data for Chart.js
const hourlyData = response.data.timelines[0].intervals;
const chartData = {
  labels: hourlyData.map(interval => 
    new Date(interval.time).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  ),
  datasets: [
    {
      label: 'Temperature (°C)',
      data: hourlyData.map(interval => interval.values.temperature),
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.4
    },
    {
      label: 'Heat Stress Threshold (25°C)',
      data: Array(hourlyData.length).fill(25),
      borderColor: 'rgb(239, 68, 68)',
      borderDash: [5, 5],
      pointRadius: 0
    }
  ]
};

// Create agricultural monitoring chart
new Chart(ctx, {
  type: 'line',
  data: chartData,
  options: {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Farm Temperature Monitoring - Next 24 Hours'
      },
      legend: {
        display: true,
        position: 'top'
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Temperature (°C)'
        },
        min: 10,
        max: 30
      }
    }
  }
});

// Irrigation logic
const currentHumidity = hourlyData[0].values.humidity;
const evapotranspirationRate = hourlyData[0].values.evapotranspiration;

if (currentHumidity < 40 && evapotranspirationRate > 0.3) {
  console.log('⚠️ Irrigation recommended - Low humidity & high ET rate');
} else if (currentHumidity > 80) {
  console.log('✅ Adequate moisture - Monitor for fungal risk');
} else {
  console.log('📊 Moisture levels normal - Continue monitoring');
}`
  },
  
  chartConfig: {
    type: 'line',
    datasets: [
      {
        label: 'Temperature (°C)',
        data: sampleApiResponse.data.timelines[0].intervals.map(interval => ({
          x: interval.time,
          y: interval.values.temperature
        })),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)'
      }
    ],
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: 'Live Farm Temperature Monitoring'
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'hour'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Temperature (°C)'
          }
        }
      }
    }
  },
  
  businessMetrics: [
    {
      title: 'Water Conservation',
      description: 'Data-driven irrigation scheduling reduces waste',
      impact: 'Potential water savings (varies by crop/climate)'
    },
    {
      title: 'Crop Monitoring',
      description: 'Temperature alerts help prevent heat stress',
      impact: 'Improved crop health monitoring capability'
    },
    {
      title: 'Decision Support',
      description: 'Real-time data for irrigation timing decisions',
      impact: 'Enhanced decision-making tools'
    },
    {
      title: 'Automation Foundation',
      description: 'Base system for IoT integration and alerts',
      impact: 'Platform for expanding farm automation'
    }
  ],
  
  tags: [
    'Chart.js',
    'IoT',
    'Precision Agriculture',
    'Real-time Monitoring',
    'Weather API',
    'Dashboard',
    'JavaScript'
  ],
  
  estimatedROI: 'Variable (depends on farm size and implementation)',
  timeToImplement: 'Implementation complexity: Intermediate'
}; 